/**
 * Bulk-edit field surface for the camera-model tree.
 *
 * A bulk field is one whose value is legitimately shared across several
 * entities. Identity fields (name, host, slot_number, view_number/name/area)
 * and credentials (username, password) are deliberately excluded: setting them
 * across a selection is either always wrong or too destructive to offer.
 *
 * Values are compared through diff.js so that "do these entities agree?" uses
 * exactly the same notion of equality as the changeset builder — otherwise a
 * field could look shared here and still be emitted as a diff on save.
 */

import { diffInternals } from "./diff";

const { normalizedEqual, DEVICE_FK_FIELDS, CAMERA_FK_FIELDS, VIEW_FK_FIELDS } = diffInternals;

/** Sentinel for "the selected entities do not agree on this field". */
export const MIXED = Symbol("MIXED");

export const BULK_FIELDS = {
  device: ["transport", "rtsp_port", "api_port", "rtsp_port_redirected", "api_port_redirected"],
  camera: ["stream_quality", "rtsp_url_suffix", "protocol", "inference_machine", "is_active"],
  view: [
    "is_active",
    "body_sensitivity",
    "bag_sensitivity",
    "relevance",
    "body_theft",
    "gesture_into_body",
    "product_in_personal_bag",
    "product_into_backpack",
    "suspicious_bag",
    "product_into_stroller",
    "client_normal_bag",
    "store_normal_bag",
    "deblistering",
    "consumption",
    "burst_shot",
    "other_suspicious",
  ],
};

const FK_FIELDS_BY_KIND = {
  device: DEVICE_FK_FIELDS,
  camera: CAMERA_FK_FIELDS,
  view: VIEW_FK_FIELDS,
};

/**
 * Whether a field of the given entity kind holds a foreign key, which may
 * arrive either as an id or as a nested {id, …} object.
 *
 * @param {string} kind - "device", "camera" or "view".
 * @param {string} field - Field name.
 * @returns {boolean} True when the field must be compared by id.
 */
export function isBulkFkField(kind, field) {
  const fields = FK_FIELDS_BY_KIND[kind];
  return fields ? fields.has(field) : false;
}

/**
 * The value the selected entities share on a field, or MIXED when they diverge.
 *
 * An empty selection has no shared value and therefore yields MIXED. When the
 * entities agree, the first entity's raw value is returned — so a field mixing
 * null and "" (equivalent under diff.js) reports the first form seen rather
 * than inventing a canonical one.
 *
 * @param {Array<Object>} entities - Selected entities.
 * @param {string} field - Field name.
 * @param {string} kind - "device", "camera" or "view".
 * @returns {*|Symbol} The shared value, or MIXED.
 */
export function commonValue(entities, field, kind) {
  const list = entities || [];
  if (list.length === 0) return MIXED;

  const isFk = isBulkFkField(kind, field);
  const first = list[0][field];
  const agrees = list.every((entity) => normalizedEqual(entity[field], first, isFk));
  return agrees ? first : MIXED;
}

/**
 * Which of the given fields carry a pending change somewhere in the selection.
 *
 * A field is dirty when at least one selected entity differs from *its own*
 * server value — the same per-entity comparison the changeset builder makes, so
 * the marker lights up exactly when saving would emit that field. A field whose
 * values merely differ between entities (MIXED in the form) is not dirty.
 *
 * Consequence, deliberate: an edit made before entering bulk mode is reported as
 * dirty here, and the per-field revert therefore undoes it too. That keeps one
 * meaning of "modified" across the screen instead of a bulk-session-only one.
 *
 * Entities with no server counterpart (TEMP, not selectable in bulk mode) are
 * skipped: a create op sends all their fields regardless.
 *
 * @param {Array<Object>} serverEntities - Server snapshots, matched by public_id.
 * @param {Array<Object>} workingEntities - Selected working entities.
 * @param {Array<string>} fields - Fields to inspect.
 * @param {string} kind - "device", "camera" or "view".
 * @returns {Set<string>} The subset of fields with a pending change.
 */
export function dirtyFields(serverEntities, workingEntities, fields, kind) {
  const serverById = new Map((serverEntities || []).map((entity) => [entity.public_id, entity]));
  const dirty = new Set();

  (workingEntities || []).forEach((working) => {
    const server = serverById.get(working.public_id);
    if (!server) return;
    (fields || []).forEach((field) => {
      if (dirty.has(field)) return;
      if (!normalizedEqual(server[field], working[field], isBulkFkField(kind, field))) dirty.add(field);
    });
  });

  return dirty;
}

/**
 * Normalise a text input value before it enters the draft: a blank string means
 * "no value" and is stored as null, matching the legacy recorder save path so
 * clearing a field behaves the same in both models. Non-string values pass
 * through untouched, so false and 0 survive.
 *
 * @param {*} value - Raw input value.
 * @returns {*} The value, or null when it is a blank string.
 */
export function coerceEmptyToNull(value) {
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
}
